import React, { useState } from 'react';
import {
  Stack, Group, Title, Text, Paper, Badge, Table, Button, Loader, ScrollArea, ActionIcon, Switch, Tooltip,
} from '@mantine/core';
import { IconPlus, IconEdit, IconPin } from '@tabler/icons-react';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import useAuthenticatedPut from '../../../hooks/useAuthenticatedPut';
import TeachModal from '../../teaching/TeachModal';
import config from '../../../config';
import DraftBadge from '../../common/DraftBadge';

const SURFACES = [
  { key: 'nymeria', label: 'AI Sales Agent', color: 'grape' },
  { key: 'headline', label: 'Headline', color: 'teal' },
  { key: 'judgement', label: 'Judgment', color: 'violet' },
];

export default function TeachingConsole() {
  const [surface, setSurface] = useState('nymeria');
  const [tick, setTick] = useState(0);
  const [modal, setModal] = useState(null);   // null | {} (new) | row (edit)
  const { put } = useAuthenticatedPut();

  const url = `${config.apiBaseUrl}/api/vesper/teaching-examples?surface=${surface}&t=${tick}`;
  const { data, isLoading } = useAuthenticatedData(url);
  const rows = Array.isArray(data) ? data : [];
  const refresh = () => setTick((t) => t + 1);

  const toggle = async (r, field) => {
    try {
      await put(`${config.apiBaseUrl}/api/vesper/teaching-examples/${r.id}`, {
        surface: r.surface, title: r.title, context: r.context, idealOutput: r.ideal_output,
        rationale: r.rationale, tags: r.tags,
        pinned: field === 'pinned' ? !(r.pinned === true || r.pinned === 1) : (r.pinned === true || r.pinned === 1),
        active: field === 'active' ? !(r.active === true || r.active === 1) : (r.active === true || r.active === 1),
      });
      refresh();
    } catch { /* likely 403 non-leader; ignore */ }
  };

  return (
    <Stack gap="md" p="md">
      <DraftBadge />
      <div>
        <Title order={2}>Teaching</Title>
        <Text size="sm" c="dimmed">
          Worked examples that shape what the agents say and how they decide — the demonstration complement
          to Guidance rules. Played into future calls as few-shot; edits take effect on the next call.
        </Text>
      </div>

      <Group justify="space-between">
        <Group gap="xs">
          {SURFACES.map((s) => (
            <Button key={s.key} size="xs" variant={surface === s.key ? 'filled' : 'light'} color={s.color}
              onClick={() => setSurface(s.key)}>{s.label}</Button>
          ))}
        </Group>
        <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setModal({})}>Add example</Button>
      </Group>

      <Paper withBorder radius="md">
        <ScrollArea>
          <Table fz="xs" striped highlightOnHover miw={900}>
            <Table.Thead><Table.Tr>
              <Table.Th>Title / situation</Table.Th><Table.Th>Ideal response</Table.Th>
              <Table.Th>Pinned</Table.Th><Table.Th>Ver</Table.Th><Table.Th>Active</Table.Th><Table.Th />
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {isLoading && <Table.Tr><Table.Td colSpan={6}><Group justify="center" p="md"><Loader size="sm" /></Group></Table.Td></Table.Tr>}
              {!isLoading && rows.map((r) => (
                <Table.Tr key={r.id} style={{ opacity: (r.active === true || r.active === 1) ? 1 : 0.5 }}>
                  <Table.Td maw={340}>
                    <Text size="xs" fw={600}>{r.title || '(untitled)'}</Text>
                    <Text size="xs" c="dimmed" lineClamp={2}>{r.context}</Text>
                  </Table.Td>
                  <Table.Td maw={380}><Text size="xs" lineClamp={3}>{r.ideal_output}</Text></Table.Td>
                  <Table.Td>
                    <Tooltip label="Pin = always inject">
                      <ActionIcon size="sm" variant={(r.pinned === true || r.pinned === 1) ? 'filled' : 'subtle'} color="yellow" onClick={() => toggle(r, 'pinned')}>
                        <IconPin size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>{r.version}</Table.Td>
                  <Table.Td><Switch size="xs" checked={r.active === true || r.active === 1} onChange={() => toggle(r, 'active')} /></Table.Td>
                  <Table.Td><Tooltip label="Edit"><ActionIcon variant="subtle" size="sm" onClick={() => setModal(r)}><IconEdit size={15} /></ActionIcon></Tooltip></Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && rows.length === 0 && <Table.Tr><Table.Td colSpan={6}><Text c="dimmed" ta="center" py="md">No examples yet for this surface.</Text></Table.Td></Table.Tr>}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      {/* Add (blank) uses TeachModal; edit reuses it prefilled (via a PUT would be ideal — for v1 edit opens prefilled create). */}
      <TeachModal
        opened={!!modal}
        onClose={() => setModal(null)}
        surface={modal?.surface || surface}
        context={modal?.context || ''}
        idealOutput={modal?.ideal_output || ''}
        onSaved={refresh}
      />
    </Stack>
  );
}
